import { EXCEL_COLUMNS, MASTER_COLUMNS, PROGRAMS } from './constants';
import moment from 'moment-timezone';

export type Program = typeof PROGRAMS[number];

export type ProgramTable = {
  rows: any[];
  total: number;
};

export const emptyTables = (): Record<Program, ProgramTable> => ({
  Master: { rows: [], total: 0 },
  Transactional: { rows: [], total: 0 },
});

export const getResponseTable = (response: any, program: Program): ProgramTable => {
  const table = response?.data?.[program.toLowerCase()];
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  const total = Number(table?.total ?? rows.length);
  return { rows, total: Number.isFinite(total) ? total : rows.length };
};

const normalizeColumnKey = (key: string) => String(key || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const stringifyDisplayValue = (value: unknown) => {
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};

const formatDateValue = (value: unknown, key: string) => {
  if (value === null || value === undefined || value === '') return '--';
  const displayValue = stringifyDisplayValue(value);

  if (key === 'silver_updated_datetime_utc') {
    const parsedValue = moment.utc(displayValue);
    return parsedValue.isValid() ? parsedValue.format('MM-DD-YYYY HH:mm') : displayValue;
  }

  if (['run_date', 'source_received_date', 'bronze_raw_run_date'].includes(key)) {
    const parsedValue = moment(displayValue, 'YYYY-MM-DD', true);
    return parsedValue.isValid() ? parsedValue.format('MM-DD-YYYY') : displayValue;
  }

  if (/^-?\d+$/.test(displayValue)) {
    return Number(displayValue).toLocaleString('en-US');
  }

  return displayValue;
};

export const getTableColumns = (rows: any[], program: Program = 'Transactional') => {
  const columns = program === 'Master' ? MASTER_COLUMNS : EXCEL_COLUMNS;
  const actualKeyMap = new Map<string, string>();
  rows.forEach((row) => Object.keys(row || {}).forEach((key) => {
    const normalizedKey = normalizeColumnKey(key);
    if (!normalizedKey || ['id', 'programname', 'program'].includes(normalizedKey)) return;
    if (!actualKeyMap.has(normalizedKey)) actualKeyMap.set(normalizedKey, key);
  }));

  return columns.map((column) => {
    const actualKey = column.aliases
      .map((alias) => actualKeyMap.get(normalizeColumnKey(alias)))
      .find((value) => value) || column.key;

    return {
      key: actualKey,
      name: column.label,
      width: program === 'Master' && column.key === 'description' ? 300 : 150,
      ...(column.key === 'run_date' ? { cellClass: 'reconciliation-run-date-cell' } : {cellClass: 'reconciliation-cell'}),
      formatter: ({ row }: any) => {
        const value = row?.[actualKey] ?? row?.[column.key] ?? row?.[column.aliases[0]];
        const formattedValue = formatDateValue(value, column.key);
        if (program !== 'Master' && column.key === 'run_date') {
          const parsedDate = moment.utc(stringifyDisplayValue(value), 'YYYY-MM-DD', true);
          const day = row?.day || (parsedDate.isValid() ? parsedDate.format('dddd') : '');
          if (day) return `${formattedValue}\n${day}`;
        }
        return formattedValue;
      },
      resizable: true,
      sortable: true,
    };
  });
};

export const sortRows = (rows: any[], sortBy: string, sortDir: string) => {
  if (!sortBy || !sortDir) return rows;
  return [...rows].sort((firstRow, secondRow) => {
    const firstValue = firstRow?.[sortBy] ?? '';
    const secondValue = secondRow?.[sortBy] ?? '';
    if (firstValue === '' && secondValue === '') return 0;
    if (firstValue === '') return 1;
    if (secondValue === '') return -1;
    const comparison = String(firstValue).localeCompare(String(secondValue), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? comparison : comparison * -1;
  });
};